import { i18n } from '@/core/i18n'
import type { NotificationOptions } from '@/stores/notifications/store'
import { computed } from 'vue'

const notifications = {
  $contactsDeleted: () => ({
    type: 'success',
    title: computed(() => i18n.global.t('notifications.contactsDeleted')),
  }),
  $contactsImported: () => ({
    type: 'success',
    title: computed(() => i18n.global.t('notifications.contactsImported')),
  }),
  $durationTooLong: () => ({
    type: 'error',
    title: computed(() => i18n.global.t('error.durationTooLong')),
  }),
  $generalError: () => ({
    type: 'error',
    title: computed(() => i18n.global.t('error.general')),
  }),
  $invalidEmail: () => ({
    type: 'error',
    title: computed(() => i18n.global.t('error.invalidEmail')),
  }),
  $invalidEmailDomain: () => ({
    type: 'error',
    title: computed(() => i18n.global.t('error.invalidEmailDomain')),
  }),
  $invalidHash: () => ({
    type: 'error',
    title: computed(() => i18n.global.t('error.invalidHash')),
  }),
  $macAlreadyInUse: () => ({
    type: 'error',
    title: computed(() => i18n.global.t('notifications.macAlreadyInUse')),
  }),
  $musicAlreadyInUse: () => ({
    type: 'error',
    title: computed(() => i18n.global.t('error.musicAlreadyInUse')),
  }),
  $networkError: () => ({
    type: 'error',
    title: computed(() => i18n.global.t('error.network')),
  }),
  $notReachable: () => ({
    type: 'error',
    title: computed(() => i18n.global.t('notifications.notReachable')),
  }),
  $promptNotFoundError: () => ({
    type: 'error',
    title: computed(() => i18n.global.t('notifications.promptNotFound')),
  }),
  $promptSecurityError: () => ({
    type: 'error',
    title: computed(() => i18n.global.t('error.networkSecurity')),
  }),
  $serverError: () => ({
    type: 'error',
    title: computed(() => i18n.global.t('error.general')),
  }),
  $validationErrors: () => ({
    type: 'error',
    title: computed(() => i18n.global.t('error.validationErrors')),
  }),
  $successMessage: () => ({
    type: 'success',
    title: computed(() => i18n.global.t('success.updateSuccess')),
  }),
  $recordCanceled: () => ({
    type: 'success',
    title: computed(() => i18n.global.t('error.recordCanceled')),
  }),
  $recordSuccessful: () => ({
    type: 'success',
    title: computed(() => i18n.global.t('success.recordSuccessful')),
  }),
  $usernameAlreadyInUse: () => ({
    type: 'error',
    title: computed(() => i18n.global.t('error.usernameAlreadyInUse')),
  }),
  $emailAlreadyInUseConnectMe: () => ({
    type: 'error',
    title: computed(() => i18n.global.t('error.emailAlreadyInUseConnectMe')),
  }),
  $emailSend: () => ({
    type: 'success',
    title: computed(() => i18n.global.t('success.emailSend')),
  }),
  $ttsQuotaLimitError: () => ({
    type: 'error',
    title: computed(() => i18n.global.t('tts.quotalimit.error')),
  }),
  $catchAllExtensionDelete: () => ({
    type: 'error',
    title: computed(() => i18n.global.t('extension.conflictMessage.catchAll')),
  }),
} satisfies Record<string, () => NotificationOptions>

export function getNotification(name: keyof typeof notifications) {
  return notifications[name]?.()
}

export function getCatchAllExtensionDeleteNotification(
  names: string[]
): NotificationOptions {
  return {
    type: 'error',
    title: computed(() => {
      const message = i18n.global.t('extension.conflictMessage.catchAll')
      return names.length ? `${names.join(', ')}: ${message}` : message
    }),
  }
}

function isInvalidHashError(error: any): boolean {
  return Array.isArray(error?.response?.data?.errors?.hash)
}

export function selectNotification(error: any): NotificationOptions {
  if (isInvalidHashError(error)) {
    return getNotification('$invalidHash')
  }

  if (error.response?.status === 400) {
    return getNotification('$promptSecurityError')
  }

  if (error.request) {
    return getNotification('$networkError')
  }

  if (error.response) {
    return getNotification('$serverError')
  }

  return getNotification('$generalError')
}
